// 運賃お伺い書ビュー（中身/TMS・Supabase連動）。trips → 相手先×月×区分 の集計を実表示し、発行→履歴を charge_sheets に保存。
// 請求（得意先）側は発行時にトークン＋明細スナップショットを付与し、得意先向け承認リンク（#/approve?token=）で
//   承認/差戻しを受け取る。差戻し・承認は realtime で担当画面へ反映。
// デモ（未接続）時は同梱サンプル(sample.trips.json＝架空データ。scripts/make-sample-data.mjs が生成)で同じ画面が動く。
import { buildSheets, listMonths, totals, nextSendState } from '../data/analytics/chargesheet.js'
import { COLLECTIONS, listDocs, upsert, subscribeChanges, invalidateCache } from '../data/collections.js'
import { supabase, supabaseReady } from '../supabase.js'
import { resolveMonth, setSelectedMonth } from '../data/selectedMonth.js'
import sampleTrips from './sample.trips.json'
import { allowSample, DEMO_TAG, noDataHtml } from '../data/demoData.js'

const YEN = (n) => '¥' + Number(n || 0).toLocaleString('ja-JP')
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
const pad = (n, l) => String(n).padStart(l, '0')
const ymc = (m) => (m || '').replace('-', '')
const ymj = (m) => (m || '').replace('-', '年') + '月'
const KLABEL = { seikyu: '請求（得意先／ご請求分）', shiharai: '支払（協力会社／お支払分）' }
const STLABEL = { issued: '発行済', sent: '送付済', confirmed: '確認済', settled: '入金/支払済', void: '無効' }
const APLABEL = { pending: '承認待ち', approved: '承認済', rejected: '差戻し' }
// 承認バッジは「--head 地 × 意味色文字」型（.tbadge・状態バッジ仕様 §7 と同族）。
// 旧「accent ベタ地 × --text」は黒 3.05:1 / 白 3.21:1 で AA 未達だった（2026-08-27 修正）
const APCOLOR = { pending: 'var(--signal)', approved: 'var(--pos)', rejected: 'var(--neg)' }

const approveUrl = (token) => `${location.origin}/#/approve?token=${encodeURIComponent(token || '')}`
const genToken = () => (globalThis.crypto && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}${Math.random().toString(16).slice(2)}${Math.random().toString(16).slice(2)}`)
const snapshotLines = (runs) => (runs || []).slice(0, 500).map((r) => ({ d: r.unko || '', a: r.a || '', b: r.b || '', kb: r.kb || '', amt: r.amt || 0, toll: r.toll || 0 }))
const isEmail = (s) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(s || '').trim())

const S = { el: null, ctx: null, months: [], month: null, kind: 'seikyu', mode: 'create', party: 0, sheets: {}, trips: [], history: [], online: false, fetchError: false, isSample: false, user: '', unsub: null, sendMsg: null }

function styleOnce() {
  if (document.getElementById('cs-style')) return
  const s = document.createElement('style'); s.id = 'cs-style'
  s.textContent = `
  .cs{display:flex;flex-direction:column;gap:12px}
  .cs-bar{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
  .cs-bar select{font-family:inherit;font-size:13px;padding:6px 10px;border:1px solid var(--e1);border-radius:8px;background:var(--card);color:var(--text)}
  .seg{display:inline-flex;background:var(--head);border-radius:9px;padding:3px}
  .seg button{border:0;background:transparent;font-family:inherit;font-size:12.5px;font-weight:700;color:var(--text);padding:6px 13px;border-radius:7px;cursor:pointer}
  .seg button.on{background:var(--accent-fill);color:#fff}
  .seg.pay button.on{background:var(--accent-fill);color:#fff}
  .seg.mode button.on{background:var(--head);color:var(--text)}
  .cs-demo{font-size:11px;color:var(--signal);background:var(--card);border:1px solid var(--accent);border-radius:999px;padding:3px 10px;margin-left:auto}
  .cs-kpis{display:flex;gap:10px;flex-wrap:wrap}
  .cs-kpi{background:var(--card);border:1px solid var(--e1);border-radius:10px;padding:9px 13px;min-width:120px}
  .cs-kpi .k{font-size:10.5px;color:var(--muted)}.cs-kpi .v{font-size:18px;font-weight:700;font-variant-numeric:tabular-nums}
  .cs-grid{display:grid;grid-template-columns:340px 1fr;gap:14px;align-items:start}
  @media(max-width:900px){.cs-grid{grid-template-columns:1fr}}
  .cs-list{border:1px solid var(--e1);border-radius:12px;overflow:hidden;background:var(--card);max-height:66vh;overflow-y:auto}
  .cs-row{display:flex;justify-content:space-between;gap:8px;padding:9px 12px;border-bottom:1px solid var(--e1);cursor:pointer}
  .cs-row:hover{background:var(--card)}.cs-row.on{background:var(--head)}
  .cs-row .nm{font-size:12.5px;font-weight:600}.cs-row .mt{font-size:10.5px;color:var(--text)}
  .cs-row .am{font-size:12.5px;font-weight:700;font-variant-numeric:tabular-nums;white-space:nowrap}
  .cs-row .st{font-size:9.5px;font-weight:800;border-radius:4px;padding:0 5px;color:var(--text);background:var(--head);margin-left:6px}
  .tbadge{font-size:9.5px;font-weight:700;color:var(--pos);background:var(--head);border-radius:4px;padding:0 5px;margin-left:5px}
  .cs-paper{border:1px solid var(--e1);border-radius:12px;background:var(--card);padding:18px 20px}
  .cs-ph{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;border-bottom:2px solid var(--e1);padding-bottom:10px;margin-bottom:8px}
  .cs-ph h3{margin:0;font-size:18px}.cs-ph .on-to{font-size:15px;font-weight:700;margin-top:6px}
  .cs-ph .meta{font-size:11px;color:var(--text);text-align:right;white-space:nowrap}
  .cs-tbl{width:100%;border-collapse:collapse;font-size:11.5px;margin-top:8px}
  .cs-tbl th,.cs-tbl td{border-bottom:1px solid var(--e1);padding:5px 7px;text-align:left;white-space:nowrap}
  .cs-tbl th{background:var(--head);color:var(--text);font-size:10.5px}
  .cs-tbl td.n,.cs-tbl th.n{text-align:right;font-variant-numeric:tabular-nums}
  .cs-foot{display:flex;justify-content:flex-end;margin-top:10px}
  .cs-foot table{border-collapse:collapse;font-size:12.5px;min-width:260px}
  .cs-foot td{padding:5px 12px}.cs-foot td.k{background:var(--head);font-weight:700;text-align:right}
  .cs-foot tr.g td{background:var(--head);color:var(--text);font-weight:700;font-size:14px}
  .cs-act{display:flex;gap:8px;align-items:center;margin-top:12px;flex-wrap:wrap}
  .cs-btn{border:0;border-radius:8px;padding:8px 16px;font-size:12.5px;font-weight:700;cursor:pointer;background:var(--pos);color:var(--bg)}
  .cs-btn:disabled{background:var(--head);cursor:not-allowed}
  .cs-btn2{border:1px solid var(--e1);background:var(--card);color:var(--text);border-radius:8px;padding:8px 12px;font-size:12px;font-weight:700;cursor:pointer}
  .cs-btn2:hover{background:var(--head)}
  .cs-note{font-size:11px;color:var(--muted)}
  .cs-badge{display:inline-block;font-size:10.5px;font-weight:800;border-radius:5px;padding:2px 8px;color:var(--text);background:var(--head);margin-left:6px}
  .cs-send{margin-top:14px;border-top:1px dashed var(--e1);padding-top:12px;display:flex;flex-direction:column;gap:9px}
  .cs-send .ttl{font-weight:800;font-size:12.5px}
  .cs-send .row{display:flex;gap:8px;flex-wrap:wrap;align-items:center}
  .cs-send input[type=email]{flex:1 1 240px;font-family:inherit;font-size:13px;padding:9px 11px;border:1px solid var(--e1);border-radius:8px}
  .cs-lnk{font-size:11px;color:var(--text);word-break:break-all;background:var(--card);border:1px solid var(--e1);border-radius:6px;padding:6px 8px}
  .cs-rejbox{margin-top:2px;background:var(--card);border:1px solid var(--e1);border-radius:8px;padding:10px 12px;font-size:12px;color:var(--neg);white-space:pre-wrap}
  .cs-okbox{margin-top:2px;background:var(--head);border:1px solid var(--e1);border-radius:8px;padding:10px 12px;font-size:12px;color:var(--pos)}
  .cs-htbl{width:100%;border-collapse:collapse;font-size:12px;background:var(--card);border:1px solid var(--e1);border-radius:12px;overflow:hidden}
  .cs-htbl th,.cs-htbl td{padding:8px 12px;border-bottom:1px solid var(--e1);text-align:left;white-space:nowrap;vertical-align:top}
  .cs-htbl th{background:var(--head);font-size:11px;color:var(--text)}
  .pill{font-size:10.5px;font-weight:700;border-radius:5px;padding:1px 7px;background:var(--head);color:var(--text)}
  .cs-rejbanner{background:var(--head);border:1px solid var(--e1);color:var(--neg);border-radius:10px;padding:10px 14px;font-size:12.5px;font-weight:800;cursor:pointer}
  .cs-rejbanner:hover{background:var(--head)}
  /* 印刷ボタンの帯。ボタンは共通層 styles.css の .btn / .btn--primary をそのまま使う
     （invoice.js と同じ作法。ここでボタンを作り直さない） */
  .cs-pbar{display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin:0 0 10px}

  /* ---------- 印刷（お伺い書1件をA4縦で刷る。VEIL-18） ----------
     請求書（invoice.js）と違い、この紙に**実物様式との 1:1 要求は無い**。
     だから請求書のように紙を width:297mm/height:210mm の固定寸法・絶対配置で組まない。
     請求書はその作りゆえに VEIL-19 ——「印刷ダイアログで左右余白を 8mm 以上にされると
     刷り面からはみ出して末尾に白紙が1枚出る」——を抱えている（CSS では吸収できないと実測済み）。
     ここはフロー要素のまま @page の余白に流す。余白設定が何であっても内容は折り返して
     次ページへ送られるだけで、欠けないし白紙も出ない。
     色はテーマ変数を一切使わず白地・黒文字の直値だけ＝ダークで開いていてもそのまま刷れる。
     地色にも頼らない: ブラウザ既定の「背景のグラフィック OFF」で th の地が消えても
     読めるよう、表は罫線と太字で組み替える。 */
  @media print {
    @page { size: A4; margin: 14mm 12mm }
    html,body{background:#fff !important}
    /* 操作UI: 月/区分/モードの切替・KPI帯・相手先一覧・発行/送付パネル・差戻し帯・デモバッジ・印刷ボタン */
    .cs-bar,.cs-kpis,.cs-list,.cs-act,.cs-send,.cs-rejbanner,.cs-demo,.cs-pbar{display:none !important}
    .cs{display:block;gap:0}
    .cs-grid{display:block}
    .cs-paper{background:#fff !important;color:#000;border:0;border-radius:0;padding:0}
    .cs-paper *{color:#000 !important}
    .cs-ph{border-bottom:2px solid #000}
    .cs-tbl th,.cs-tbl td{border-bottom:1px solid #666;background:#fff !important}
    .cs-tbl th{font-weight:700;border-bottom:1.5px solid #000}
    .cs-tbl thead{display:table-header-group}
    .cs-tbl tr{break-inside:avoid;page-break-inside:avoid}
    .cs-foot{break-inside:avoid;page-break-inside:avoid}
    .cs-foot td{background:#fff !important;border-bottom:1px solid #666}
    .cs-foot tr.g td{border-bottom:2px solid #000}
    .cs-badge,.tbadge{background:#fff !important;border:1px solid #000}
  }
  `
  document.head.appendChild(s)
}

export async function renderChargesheet(el, ctx = {}) {
  styleOnce()
  S.el = el; S.ctx = ctx
  S.online = !ctx.demo && supabaseReady
  S.fetchError = false
  S.user = ctx.user?.email || ''
  el.innerHTML = '<div class="ct-state is-loading"><b class="ct-state__t">読み込んでいます…</b><span>対象の運行実績を集計しています</span></div>'
  let trips = sampleTrips
  if (S.online) {
    // 実データ0件のときサンプルへ落ちてよいかは共通判定 allowSample（設定 settings/display）に委ねる
    try { const t = await listDocs(COLLECTIONS.trips); if (t && t.length) trips = t; else if (!allowSample(S.online)) trips = [] }
    catch { S.online = false; S.fetchError = true }
  }
  // サインイン済みの実fetch失敗はサンプルへフォールバックせず is-error を主表示にする
  // （サンプル描画は明示的なデモ状況＝online初期値false のときだけ。2026-08-27 負債解消）
  if (S.fetchError) {
    if (S.unsub) { try { S.unsub() } catch (_) { /* noop */ } S.unsub = null }
    S.trips = []; S.sheets = {}; S.history = []; S.isSample = false
    paintFetchError(); return
  }
  S.isSample = trips === sampleTrips
  // 実データ0件＋デモ表示OFF＝正直に空を出す（架空の実績を本物の顔で並べない。2026-08-27 案C）
  if (!S.isSample && !trips.length) {
    if (S.unsub) { try { S.unsub() } catch (_) { /* noop */ } S.unsub = null }
    S.trips = []; S.sheets = {}; S.history = []
    paintNoData(); return
  }
  S.trips = trips
  S.sheets = buildSheets(trips)
  const picked = resolveMonth(listMonths(S.sheets))
  S.months = picked.months
  S.month = picked.month
  S.party = 0
  S.history = []
  if (S.online) { try { S.history = await listDocs(COLLECTIONS.chargeSheets) } catch { /* noop */ } }

  // realtime: 得意先の承認/差戻し（RPC経由の charge_sheets 更新）を担当画面へ反映
  if (S.unsub) { try { S.unsub() } catch (_) { /* noop */ } S.unsub = null }
  if (S.online) {
    try {
      S.unsub = subscribeChanges(COLLECTIONS.chargeSheets, async () => {
        if (!S.el || !S.el.querySelector('.cs')) return // 他ビューへ移動中はスキップ
        try { invalidateCache(COLLECTIONS.chargeSheets); S.history = await listDocs(COLLECTIONS.chargeSheets) } catch (_) { /* noop */ }
        const ae = document.activeElement
        if (ae && S.el.contains(ae) && /INPUT|TEXTAREA/.test(ae.tagName)) return // 入力中は描き直さない
        paint()
      })
    } catch (_) { /* noop */ }
  }
  paint()
}

// 実fetch失敗（デモ意図とは別）の主表示。サンプルデータは描画しない
function paintFetchError() {
  S.el.innerHTML = '<section class="cs"><div class="ct-state is-error"><b class="ct-state__t">データを読み込めませんでした</b><span>保存済みの実データを取得できませんでした。誤認を避けるためサンプルは表示していません。接続を確認して再試行してください</span><button type="button" class="btn" id="cs-retry">再試行</button></div></section>'
  const retry = S.el.querySelector('#cs-retry'); if (retry) retry.addEventListener('click', () => { renderChargesheet(S.el, S.ctx) })
}

// 実データ0件＋デモデータ表示OFF（既定）の主表示。サンプルは描かない
function paintNoData() {
  S.el.innerHTML = `<section class="cs">${noDataHtml('運賃お伺い書')}</section>`
}

function currentList() { return (S.sheets[S.month] && S.sheets[S.month][S.kind]) || [] }
function seqFor(month, kind) { return S.history.filter((h) => h.targetYm === month && h.kind === kind).length + 1 }
function issueNo(month, kind, seq) { return `AO-${ymc(month)}-${kind === 'seikyu' ? '1' : '2'}-${pad(seq, 4)}` }
function findIssued(g) {
  return S.history.find((h) => h.targetYm === S.month && h.kind === S.kind
    && ((g.code != null && h.partyCode === g.code && (h.partyBranchNo ?? null) === (g.branchNo ?? null)) || h.party === g.name))
}
// 送付パネルのメッセージ。realtime(subscribeChanges)による再描画で消えないよう状態に持たせ、paint() が描き直す。
// DOM へも即時反映するのは、再描画を待たずにその場で見せるため。
function sendMsg(t, color) {
  S.sendMsg = t ? { text:t, color: color || 'var(--text)' } : null
  const m = S.el && S.el.querySelector('#cs-sendmsg')
  if (m) { m.textContent = t || ''; m.style.color = color || 'var(--text)' }
}

function paint() {
  const el = S.el
  const isPay = S.kind === 'shiharai'
  const opts = S.months.map((m) => `<option value="${m}" ${m === S.month ? 'selected' : ''}>${ymj(m)}</option>`).join('')
  const T = totals(S.sheets, S.month, S.kind)
  const rejected = S.history.filter((h) => h.kind === 'seikyu' && h.approvalStatus === 'rejected' && h.targetYm === S.month)
  const rejBanner = rejected.length
    ? `<div class="cs-rejbanner" id="cs-rejbanner">⚠ 得意先からの差戻し ${rejected.length}件（${esc(rejected.slice(0, 3).map((r) => r.party).join('、'))}${rejected.length > 3 ? ' ほか' : ''}）— クリックで発行履歴を確認</div>`
    : ''
  el.innerHTML = `
    <section class="cs">
      <div class="cs-bar">
        <select id="cs-month">${opts}</select>
        <div class="seg ${isPay ? 'pay' : ''}">
          <button data-kind="seikyu" class="${S.kind === 'seikyu' ? 'on' : ''}">請求（得意先）</button>
          <button data-kind="shiharai" class="${isPay ? 'on' : ''}">支払（協力会社）</button>
        </div>
        <div class="seg mode">
          <button data-mode="create" class="${S.mode === 'create' ? 'on' : ''}">作成</button>
          <button data-mode="history" class="${S.mode === 'history' ? 'on' : ''}">発行履歴${S.online ? '' : '（要接続）'}</button>
        </div>
        ${S.isSample ? `<span class="cs-demo">${DEMO_TAG}</span>` : ''}
      </div>
      ${rejBanner}
      <div class="cs-kpis">
        <div class="cs-kpi"><div class="k">相手先</div><div class="v">${T.parties}</div></div>
        <div class="cs-kpi"><div class="k">件数</div><div class="v">${T.n}</div></div>
        <div class="cs-kpi"><div class="k">${isPay ? '傭車料' : '運賃'}（税抜）</div><div class="v">${YEN(T.sub)}</div></div>
        <div class="cs-kpi"><div class="k">消費税</div><div class="v">${YEN(T.tax)}</div></div>
        <div class="cs-kpi"><div class="k">立替通行料</div><div class="v">${YEN(T.toll)}</div></div>
        <div class="cs-kpi"><div class="k">合計</div><div class="v">${YEN(T.total)}</div></div>
      </div>
      <div id="cs-main"></div>
    </section>`
  el.querySelector('#cs-month').addEventListener('change', (e) => { S.month = setSelectedMonth(e.target.value) || e.target.value; S.party = 0; S.sendMsg = null; paint() })
  el.querySelectorAll('[data-kind]').forEach((b) => b.addEventListener('click', () => { S.kind = b.dataset.kind; S.party = 0; S.sendMsg = null; paint() }))
  el.querySelectorAll('[data-mode]').forEach((b) => b.addEventListener('click', () => { S.mode = b.dataset.mode; paint() }))
  const rb = el.querySelector('#cs-rejbanner'); if (rb) rb.addEventListener('click', () => { S.mode = 'history'; paint() })
  if (S.mode === 'history') paintHistory()
  else paintCreate()
}

function paintCreate() {
  const list = currentList()
  const main = S.el.querySelector('#cs-main')
  if (!list.length) {
    main.innerHTML = S.trips.length
      ? '<div class="ct-state is-nomatch"><b class="ct-state__t">この月・区分の対象がありません</b><span>月または区分を切り替えてください</span></div>'
      : '<div class="ct-state is-empty"><b class="ct-state__t">運行実績がまだありません</b><span>運行実績を取り込むと、ここに請求・支払の対象が表示されます</span></div>'
    return
  }
  if (S.party >= list.length) S.party = 0
  const rows = list.map((g, i) => {
    const iss = findIssuedFor(g)
    const ap = iss && S.kind === 'seikyu' ? (iss.approvalStatus || 'pending') : null
    const stTag = ap ? `<span class="st" style="color:${APCOLOR[ap]}">${APLABEL[ap]}</span>` : (iss ? '<span class="st">発行済</span>' : '')
    return `
    <div class="cs-row ${i === S.party ? 'on' : ''}" data-i="${i}">
      <div><div class="nm">${esc(g.name)}${g.tnum ? '<span class="tbadge">T</span>' : ''}${stTag}</div>
        <div class="mt">${g.n}件 ${g.code != null ? '・CD ' + pad(g.code, 6) + (g.branchNo != null ? '-' + g.branchNo : '') : ''}</div></div>
      <div class="am">${YEN(g.total)}</div>
    </div>`
  }).join('')
  main.innerHTML = `<div class="cs-grid"><div class="cs-list">${rows}</div><div id="cs-paper"></div></div>`
  main.querySelectorAll('.cs-row').forEach((r) => r.addEventListener('click', () => { S.party = +r.dataset.i; S.sendMsg = null; paint() }))
  paintPaper(list[S.party])
}

// 一覧行用（S.kind固定で対象の発行を探す）
function findIssuedFor(g) {
  return S.history.find((h) => h.targetYm === S.month && h.kind === S.kind
    && ((g.code != null && h.partyCode === g.code && (h.partyBranchNo ?? null) === (g.branchNo ?? null)) || h.party === g.name))
}

function paintPaper(g) {
  const box = S.el.querySelector('#cs-paper'); if (!g) return
  const isPay = S.kind === 'shiharai'
  const isSeikyu = !isPay
  const already = findIssued(g)
  const ap = already && isSeikyu ? (already.approvalStatus || 'pending') : null
  const apBadge = ap ? `<span class="cs-badge" style="color:${APCOLOR[ap]}">${APLABEL[ap]}</span>` : ''
  const lines = g.runs.slice(0, 200).map((r) => `<tr>
      <td>${esc(r.unko)}</td><td>${esc(r.a)}→${esc(r.b)}</td><td>${esc(r.kb)}</td>
      <td class="n">${YEN(r.amt)}</td><td class="n">${r.toll ? YEN(r.toll) : '—'}</td></tr>`).join('')
  const more = g.runs.length > 200 ? `<tr><td colspan="5" class="cs-note">…ほか ${g.runs.length - 200} 件</td></tr>` : ''
  const seq = seqFor(S.month, S.kind)

  // ---- 発行/送付アクション ----
  let actionHtml
  if (!already) {
    actionHtml = `<div class="cs-act">
      <button class="cs-btn" id="cs-issue" ${S.online ? '' : 'disabled'}>発行（確定）</button>
      ${S.online
        ? `<span class="cs-note">${isSeikyu ? '確定すると履歴に保存し、得意先へ承認用リンクを送れます。' : '確定すると履歴に保存します。'}</span>`
        : '<span class="cs-note">接続時に発行可能</span>'}
    </div>`
  } else if (isPay) {
    actionHtml = `<div class="cs-act"><button class="cs-btn" disabled>発行済み</button>
      <span class="cs-note">✓ ${esc(already.issueNo)}・${STLABEL[already.status] || esc(already.status)}</span></div>`
  } else if (!already.token) {
    // 旧版（このお伺い書機能以前の発行）＝トークン無し → 再発行でリンク付与
    actionHtml = `<div class="cs-act"><button class="cs-btn" disabled>発行済み</button>
      <span class="cs-note">承認リンク未付与（旧版）</span>
      <button class="cs-btn2" id="cs-reissue">承認リンクを付与</button></div>`
  } else {
    const url = approveUrl(already.token)
    const sendSt = nextSendState(already.status) // null＝送付の前後にいない発行（確認済/入金済/無効）
    const apDetail = ap === 'rejected'
      ? `<div class="cs-rejbox"><b>差戻しコメント</b>${already.approverName ? '（' + esc(already.approverName) + '）' : ''}：
${esc(already.approvalComment || '')}</div>`
      : ap === 'approved'
        ? `<div class="cs-okbox">✓ ${esc(already.approverName || '得意先')} が承認（${esc((already.approvalAt || '').slice(0, 10))}）— この内容で請求書発行に進めます</div>`
        : `<div class="cs-note">承認待ち。得意先がリンクから承認/差戻しします。${already.viewedAt ? '（開封済 ' + esc((already.viewedAt || '').slice(0, 10)) + '）' : '（未開封）'}</div>`
    actionHtml = `<div class="cs-send">
      <div class="ttl">得意先へ確認依頼（承認用リンク）${apBadge}</div>
      ${apDetail}
      <div class="row">
        <input type="email" id="cs-email" placeholder="送付先メール（例：keiri@example.co.jp）" value="${esc(already.customerEmail || '')}">
        <button class="cs-btn" id="cs-sendmail">メール送信</button>
      </div>
      <div class="row">
        <button class="cs-btn2" id="cs-copy">リンクをコピー</button>
        <button class="cs-btn2" id="cs-mailto">メール下書きを開く</button>
        <button class="cs-btn2" id="cs-open">リンクを開く</button>
        ${sendSt ? `<button class="cs-btn2" id="cs-marksent">${sendSt.label}</button>` : ''}
      </div>
      <div class="cs-lnk" id="cs-url">${esc(url)}</div>
      <div class="cs-note" id="cs-sendmsg" style="color:${S.sendMsg ? esc(S.sendMsg.color) : 'var(--text)'}">${S.sendMsg ? esc(S.sendMsg.text) : ''}</div>
    </div>`
  }

  box.innerHTML = `
    <div class="cs-pbar">
      <button type="button" class="btn btn--primary" id="cs-print">印刷 / PDFで保存</button>
      <span class="cs-note">選択中の1件をA4縦で刷ります。左の相手先一覧・上の切替・発行/送付の操作は紙に出ません。</span>
    </div>
    <div class="cs-paper">
      <div class="cs-ph">
        <div><h3>運賃お伺い書${apBadge}</h3><div class="on-to">${esc(g.name)} 御中</div>
          ${isPay && g.tnum ? `<div class="cs-note">登録番号：${esc(g.tnum)}</div>` : ''}</div>
        <div class="meta">対象：${esc(ymj(S.month))}<br>区分：${KLABEL[S.kind]}<br>
          発行番号：${already ? esc(already.issueNo) : issueNo(S.month, S.kind, seq) + '（未発行）'}<br>件数：${g.n}</div>
      </div>
      <table class="cs-tbl">
        <thead><tr><th>運行日</th><th>発→着</th><th>車種</th><th class="n">${isPay ? '傭車料' : '運賃'}</th><th class="n">立替通行料</th></tr></thead>
        <tbody>${lines}${more}</tbody>
      </table>
      <div class="cs-foot"><table>
        <tr><td class="k">${isPay ? '傭車料' : '運賃'}小計（税抜）</td><td class="n">${YEN(g.sub)}</td></tr>
        <tr><td class="k">消費税（10%）</td><td class="n">${YEN(g.tax)}</td></tr>
        <tr><td class="k">立替通行料（非課税）</td><td class="n">${YEN(g.toll)}</td></tr>
        <tr class="g"><td class="k" style="color:var(--text);background:transparent">ご${isPay ? 'お支払' : '請求'}金額</td><td class="n">${YEN(g.total)}</td></tr>
      </table></div>
      ${actionHtml}
    </div>`

  const printBtn = box.querySelector('#cs-print')
  if (printBtn) printBtn.addEventListener('click', () => window.print())

  const issueBtn = box.querySelector('#cs-issue')
  if (issueBtn && S.online && !already) issueBtn.addEventListener('click', () => issue(g))
  const reBtn = box.querySelector('#cs-reissue')
  if (reBtn) reBtn.addEventListener('click', () => attachApproval(already, g))

  // 送付パネル
  const emailEl = box.querySelector('#cs-email')
  const url = already && already.token ? approveUrl(already.token) : ''
  const copyBtn = box.querySelector('#cs-copy')
  if (copyBtn) copyBtn.addEventListener('click', async () => {
    try { await navigator.clipboard.writeText(url); sendMsg('リンクをコピーしました。メール等で得意先へ送ってください。', 'var(--pos)') }
    catch { sendMsg('コピーできませんでした。下のURLを選択してコピーしてください。', 'var(--neg)') }
  })
  const openBtn = box.querySelector('#cs-open')
  if (openBtn) openBtn.addEventListener('click', () => window.open(url, '_blank', 'noopener'))
  const mailtoBtn = box.querySelector('#cs-mailto')
  if (mailtoBtn) mailtoBtn.addEventListener('click', () => {
    const to = (emailEl && emailEl.value.trim()) || (already.customerEmail || '')
    const subj = `【エースジャパン】${ymj(S.month)} 運賃お伺い書のご確認`
    const body = `${already.party} 御中\n\nいつもお世話になっております。エースジャパンです。\n${ymj(S.month)}分の運賃お伺い書をお送りいたします。\n下記リンクよりご確認いただき、内容に相違なければ「承認」を、修正点がございましたら「差戻し」からお知らせください。\n\n${url}\n\n何卒よろしくお願いいたします。`
    window.location.href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(body)}`
  })
  const sendBtn = box.querySelector('#cs-sendmail')
  if (sendBtn) sendBtn.addEventListener('click', () => sendMail(already, emailEl))
  const markBtn = box.querySelector('#cs-marksent')
  if (markBtn) markBtn.addEventListener('click', () => markSent(already, markBtn))
}

async function issue(g) {
  const btn = S.el.querySelector('#cs-issue'); if (btn) { btn.disabled = true; btn.textContent = '発行中…' }
  const seq = seqFor(S.month, S.kind)
  const no = issueNo(S.month, S.kind, seq)
  const id = `${S.kind}_${ymc(S.month)}_${g.code != null ? pad(g.code, 6) + (g.branchNo != null ? '-' + g.branchNo : '') : g.name}_r1`
  const doc = {
    targetYm: S.month, kind: S.kind, party: g.name,
    partyCode: g.code ?? null, partyBranchNo: g.branchNo ?? null, revision: 1,
    issueNo: no, status: 'issued', n: g.n, amount: g.total, sub: g.sub, tax: g.tax, toll: g.toll,
    tnum: g.tnum || '', issuedAt: new Date().toISOString(), user: S.user,
  }
  if (S.kind === 'seikyu') { doc.token = genToken(); doc.approvalStatus = 'pending'; doc.lines = snapshotLines(g.runs) }
  try {
    await upsert(COLLECTIONS.chargeSheets, id, doc)
    const existing = S.history.findIndex((h) => h.id === id)
    if (existing >= 0) S.history[existing] = { id, ...doc }; else S.history.push({ id, ...doc })
    paint()
  } catch (e) {
    if (btn) { btn.disabled = false; btn.textContent = '発行（確定）' }
    alert('発行に失敗しました: ' + (e && e.message || e))
  }
}

// 旧版（トークン無し）の発行にリンク＋明細スナップショットを後付け
async function attachApproval(already, g) {
  const patch = { token: genToken(), approvalStatus: already.approvalStatus || 'pending', lines: snapshotLines(g.runs) }
  try {
    await upsert(COLLECTIONS.chargeSheets, already.id, patch)
    Object.assign(already, patch)
    invalidateCache(COLLECTIONS.chargeSheets)
    try { S.history = await listDocs(COLLECTIONS.chargeSheets) } catch (_) { /* noop */ }
    paint()
  } catch (e) { alert('リンク付与に失敗しました: ' + (e && e.message || e)) }
}

// 手動送付（リンクをコピー等）のあと、発行の送付状態を切り替える。自動送信は使わない運用のため。
// 遷移の可否と中身は nextSendState() が持つ（純関数・test_chargesheet_sendstate.mjs で検証）。
async function markSent(already, btn) {
  const st = nextSendState(already.status)
  if (!st) return
  if (btn) btn.disabled = true
  try {
    let who = null
    try { const { data } = await supabase.auth.getUser(); who = (data && data.user && data.user.email) || null } catch (_) { /* 取れなくても更新は通す */ }
    const { patch } = nextSendState(already.status, new Date(), who)
    await upsert(COLLECTIONS.chargeSheets, already.id, patch)
    Object.assign(already, patch)
    invalidateCache(COLLECTIONS.chargeSheets)
    try { S.history = await listDocs(COLLECTIONS.chargeSheets) } catch (_) { /* noop */ }
    paint() // 先に描き直す。sendMsg は再生成後の #cs-sendmsg に書くため順序が重要
    sendMsg(patch.status === 'sent' ? '✓ 送付済にしました。' : '未送付に戻しました。', patch.status === 'sent' ? 'var(--pos)' : 'var(--text)')
  } catch (e) {
    if (btn) btn.disabled = false
    sendMsg('状態の更新に失敗しました: ' + ((e && e.message) || e), 'var(--neg)')
  }
}

async function sendMail(already, emailEl) {
  const email = (emailEl && emailEl.value.trim()) || ''
  if (!isEmail(email)) { sendMsg('送付先メールアドレスを入力してください。', 'var(--neg)'); if (emailEl) emailEl.focus(); return }
  const btn = S.el.querySelector('#cs-sendmail'); if (btn) { btn.disabled = true; btn.textContent = '送信中…' }
  try {
    await upsert(COLLECTIONS.chargeSheets, already.id, { customerEmail: email }) // 宛先を記録（次回プリフィル＋関数が参照）
    already.customerEmail = email
    const { data, error } = await supabase.functions.invoke('send-oki', { body: { id: already.id } })
    if (error) throw error
    if (data && data.ok === false) throw new Error(data.error || 'send failed')
    sendMsg('✓ メールを送信しました（' + email + '）。', 'var(--pos)')
    if (btn) { btn.disabled = false; btn.textContent = 'メール送信' }
  } catch (e) {
    if (btn) { btn.disabled = false; btn.textContent = 'メール送信' }
    sendMsg('自動送信が使えませんでした（未設定の可能性）。「リンクをコピー」か「メール下書きを開く」で送付してください。設定は管理者へご連絡ください。', 'var(--neg)')
  }
}

function paintHistory() {
  const main = S.el.querySelector('#cs-main')
  if (!S.online) { main.innerHTML = '<div class="cs-note" style="padding:16px">発行履歴は接続時に表示されます（charge_sheets）。</div>'; return }
  const hs = S.history.filter((h) => h.targetYm === S.month).sort((a, b) => (a.issueNo < b.issueNo ? 1 : -1))
  if (!hs.length) { main.innerHTML = '<div class="cs-note" style="padding:16px">この月の発行履歴はまだありません。「作成」から発行してください。</div>'; return }
  const apCell = (h) => {
    if (h.kind !== 'seikyu') return '<span class="cs-note">—</span>'
    const ap = h.approvalStatus || (h.token ? 'pending' : '')
    if (!ap) return '<span class="cs-note">—</span>'
    return `<span class="cs-badge" style="color:${APCOLOR[ap]};margin:0">${APLABEL[ap]}</span>`
      + (ap === 'rejected' && h.approvalComment ? `<div class="cs-note" style="color:var(--neg);white-space:normal;max-width:300px;margin-top:4px">${esc(h.approvalComment)}</div>` : '')
  }
  const rows = hs.map((h) => `<tr>
      <td>${esc(h.issueNo)}</td><td><span class="pill">${h.kind === 'seikyu' ? '請求' : '支払'}</span></td>
      <td>${esc(h.party)}</td><td class="n" style="text-align:right">${YEN(h.amount)}</td>
      <td>${apCell(h)}</td>
      <td>${STLABEL[h.status] || esc(h.status)}</td><td class="cs-note">${esc((h.issuedAt || '').slice(0, 10))}</td></tr>`).join('')
  main.innerHTML = `<table class="cs-htbl">
    <thead><tr><th>発行番号</th><th>区分</th><th>相手先</th><th style="text-align:right">金額</th><th>承認</th><th>状態</th><th>発行日</th></tr></thead>
    <tbody>${rows}</tbody></table>`
}
